/**
 * lazyWithReload — survive a deploy that happens while a tab is open.
 *
 * Lazily-loaded chunks are content-hashed, so a deploy replaces
 * `MainViewModelLayer-D7DBOLWp.js` with a differently-named file. A tab that
 * loaded the OLD index still asks for the old name; the server has no such
 * file, the SPA fallback answers with index.html, and the browser refuses it:
 *
 *   Failed to load module script: Expected a JavaScript-or-Wasm module script
 *   but the server responded with a MIME type of "text/html".
 *
 * Nothing is broken — the page is simply stale, and a reload fixes it. Without
 * this the whole app hits the error boundary the first time someone opens a
 * lazily-loaded surface (the Model panel, the Vectorisation page) after a
 * release, which is exactly when they are least expecting it.
 *
 * Reloading is safe here because the failure happens BEFORE the chunk runs, so
 * there is no half-applied state to lose. It still costs any unsaved work in
 * the tab, which is why it is once-only rather than a retry loop.
 */

import { lazy } from 'react';
import { runningBundle, liveBundle } from './updateAvailable.js';

/** Long enough to cover a reload, short enough not to mask a real outage. */
const COOLDOWN_MS = 15000;
const KEY = 'pidly.chunkReloadAt';
/** The reloads this tab made for stale chunks, as timestamps. Several deploys
 *  in a row (a busy afternoon) can leave a tab stale again moments after it
 *  reloaded; it may reload a few times, never forever. */
const LIST_KEY = 'pidly.chunkReloads';
const MAX_RELOADS = 4;
const RELOAD_WINDOW_MS = 3 * 60 * 1000;

/** Is this the browser refusing a chunk that is not on the server any more? */
export function isChunkLoadError(err) {
  const msg = String(err?.message || err || '');
  return /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Expected a JavaScript.*module script|Loading chunk .* failed|Load failed/i.test(msg);
}

function recentReloads() {
  try { return (JSON.parse(sessionStorage.getItem(LIST_KEY) || '[]') || []).filter(t => Date.now() - t < RELOAD_WINDOW_MS); } catch { return []; }
}
function reloadNow(list) {
  try { sessionStorage.setItem(LIST_KEY, JSON.stringify([...list, Date.now()])); } catch { /* quota */ }
  try { sessionStorage.setItem(KEY, String(Date.now())); } catch { /* quota */ }
  window.location.reload();
  // Never settles: the reload takes over before React can render anything
  // from this promise.
  return new Promise(() => {});
}

export default function lazyWithReload(factory) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      // IS THE TAB STALE? Ask the server which bundle it serves now. A
      // different one means a deploy landed since this tab loaded and its
      // chunks are gone: reload, as often as deploys keep landing (bounded).
      // The same one means the chunk is genuinely unreachable (offline, a
      // bad deploy): the error boundary says so. The old rule reloaded once
      // and gave up for 15 s, and a tab that reloaded into a build that was
      // itself replaced seconds later fell into the boundary on the second
      // failure, in the middle of a run of deploys.
      let stale = null;
      try {
        const mine = runningBundle();
        const live = await liveBundle();
        if (mine && live) stale = live !== mine;
      } catch { stale = null; }
      const reloads = recentReloads();
      if (stale === true) {
        if (reloads.length >= MAX_RELOADS) throw err;
        return reloadNow(reloads);
      }
      if (stale === false) throw err;
      // Unknown (offline, or the host did not answer): the old once-only rule.
      let last = 0;
      try { last = Number(sessionStorage.getItem(KEY) || 0); } catch { /* private mode */ }
      if (Date.now() - last < COOLDOWN_MS) throw err;
      return reloadNow(reloads);
    }
  });
}

/**
 * warmLazyChunks — fetch the lazily-split surfaces ONCE, shortly after
 * boot, while the tab's index.html still matches the deployed assets.
 *
 * The reload-on-stale-chunk above is the safety net, but it fires on the
 * CLICK — someone opens the Model/Twin panel after a deploy and the page
 * reloads under them, or (within the cooldown) the panel sits in its
 * loading fallback with no buttons. Warming the chunks at boot means the
 * code is already in the module cache long before any deploy can
 * invalidate it, so the click is instant and the net almost never fires.
 *
 * requestIdleCallback keeps it off the critical path; failures are
 * swallowed — this is a prefetch, not a promise.
 */
// Pages a person reaches BEFORE signing in. Warming the whole app there is
// pure cost: it was 61 scripts and ~9 MB of JavaScript fetched AND RUN behind
// the sign-in form (import() executes each module), which on a phone held
// the page blank for seconds and left taps doing nothing while it parsed.
const PUBLIC_PATHS = /^\/(login|verify|reset-password|join|confirm-email|signup|register)(\/|$)/i;

/** Whether this device should prefetch the app at all. */
function shouldWarm() {
  if (typeof window === 'undefined') return false;
  if (PUBLIC_PATHS.test(window.location.pathname || '')) return false;
  // A phone pays for every byte twice (network and a slow CPU); it fetches
  // each page when it is opened instead.
  if (window.matchMedia?.('(max-width: 768px)').matches) return false;
  const c = navigator.connection;
  if (c && (c.saveData || /(^|-)2g$/.test(c.effectiveType || ''))) return false;
  return true;
}

let queue = [];
let started = false;
/** One chunk at a time, each in an idle slot, so the warm-up never holds the
 *  main thread for long and stops the moment the page is busy. */
function drain() {
  const next = queue.shift();
  if (!next) return;
  const go = () => {
    let p;
    try { p = next(); } catch { p = null; }
    Promise.resolve(p).catch(() => { /* prefetch only */ }).finally(() => {
      if (typeof requestIdleCallback === 'function') requestIdleCallback(drain, { timeout: 4000 });
      else setTimeout(drain, 300);
    });
  };
  if (typeof requestIdleCallback === 'function') requestIdleCallback(go, { timeout: 4000 });
  else setTimeout(go, 300);
}

export function warmLazyChunks(factories) {
  if (!shouldWarm()) return;
  queue = queue.concat(factories);
  if (started) return;
  started = true;
  // Not until the page has loaded and painted, and then a little longer.
  const begin = () => setTimeout(drain, 5000);
  if (document.readyState === 'complete') begin();
  else window.addEventListener('load', begin, { once: true });
}
